import { Breadcrumb } from 'antd';
import { useMemo, type ReactNode } from 'react';
import buildCollapsedBreadcrumbItems from '../../../utils/breadcrumbUtils';
import TruncatedTooltip from '../../Common/TruncatedTooltip';

export interface FolderPickerPathItem {
  uid: number;
  fileId: string;
  title: string;
}

export interface FolderPickerBreadcrumbProps {
  listRootLabel: string;
  onListRootClick: () => void;
  path?: FolderPickerPathItem[];
  currentTitle?: string;
  currentId?: number;
  onPathNavigate: (fileId: string) => void;
  className?: string;
}

export function FolderPickerBreadcrumb({
  listRootLabel,
  onListRootClick,
  path,
  currentTitle,
  currentId,
  onPathNavigate,
  className,
}: FolderPickerBreadcrumbProps) {
  const rawItems = useMemo(() => {
    const items: { key: string; title: ReactNode }[] = [
      {
        key: 'list-root',
        title: (
          <button
            type="button"
            className="underline-hover max-w-[150px] cursor-pointer truncate border-0 bg-transparent p-0 text-left text-sm"
            onClick={onListRootClick}
          >
            <TruncatedTooltip>{listRootLabel}</TruncatedTooltip>
          </button>
        ),
      },
    ];

    path?.forEach((folder) => {
      const isCurrent = currentId != null && folder.uid === currentId;
      items.push({
        key: `path-${folder.fileId}`,
        title: isCurrent ? (
          <span className="inline-block max-w-[150px] truncate text-sm">
            <TruncatedTooltip>{folder.title}</TruncatedTooltip>
          </span>
        ) : (
          <button
            type="button"
            className="underline-hover max-w-[150px] cursor-pointer truncate border-0 bg-transparent p-0 text-left text-sm"
            onClick={() => onPathNavigate(folder.fileId)}
          >
            <TruncatedTooltip>{folder.title}</TruncatedTooltip>
          </button>
        ),
      });
    });

    if (currentTitle && (currentId == null || !path?.some((p) => p.uid === currentId))) {
      items.push({
        key: 'current',
        title: (
          <span className="inline-block max-w-[150px] truncate text-sm">
            <TruncatedTooltip>{currentTitle}</TruncatedTooltip>
          </span>
        ),
      });
    }

    return items;
  }, [currentId, currentTitle, listRootLabel, onListRootClick, onPathNavigate, path]);

  const items = useMemo(
    () => buildCollapsedBreadcrumbItems(rawItems, { maxVisible: 3 }) ?? [],
    [rawItems],
  );

  return (
    <div className={className ? `${className} min-w-0 overflow-hidden` : 'min-w-0 overflow-hidden'}>
      <Breadcrumb className="[&_li]:min-w-0 [&_ol]:flex-nowrap!" items={items} />
    </div>
  );
}

export default FolderPickerBreadcrumb;
